import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    type ReactNode,
} from "react";

export type SupportedLanguage = "id" | "en";
export type SupportedThemeMode = "system" | "light" | "dark";
export type EffectiveThemeMode = Exclude<SupportedThemeMode, "system">;

interface PreferencesContextValue {
    language: SupportedLanguage;
    themeMode: SupportedThemeMode;
    effectiveThemeMode: EffectiveThemeMode;
    setLanguage: (language: SupportedLanguage) => void;
    setThemeMode: (mode: SupportedThemeMode) => void;
}

/**
 * Mirrors the Flexv Portal `UserPreferencesContext` contract.
 *
 * The Portal persists these to `PUT /auth/me/preferences`; the Coach API does not
 * expose that endpoint yet, so we persist locally and keep the same setter shape.
 * Swapping the storage layer for an API call later needs no caller changes.
 */
const LANGUAGE_STORAGE_KEY = "flexv_coach_language";
const THEME_STORAGE_KEY = "flexv_coach_theme_mode";

const PreferencesContext = createContext<PreferencesContextValue | undefined>(undefined);

const normalizeLanguage = (value?: string | null): SupportedLanguage => {
    return (value || "").trim().toLowerCase() === "en" ? "en" : "id";
};

const normalizeThemeMode = (value?: string | null): SupportedThemeMode => {
    const normalized = (value || "").trim().toLowerCase();
    if (normalized === "light" || normalized === "dark" || normalized === "system") {
        return normalized;
    }
    return "system";
};

const getSystemThemeMode = (): EffectiveThemeMode => {
    if (typeof window === "undefined") return "light";
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

const resolveEffectiveThemeMode = (mode: SupportedThemeMode): EffectiveThemeMode => {
    return mode === "system" ? getSystemThemeMode() : mode;
};

function readStoredValue(key: string): string | null {
    try {
        return localStorage.getItem(key);
    } catch {
        // Storage unavailable (private mode / SSR) — fall back to defaults.
        return null;
    }
}

function writeStoredValue(key: string, value: string): void {
    try {
        localStorage.setItem(key, value);
    } catch {
        // ignore
    }
}

function applyToDom(
    mode: SupportedThemeMode,
    effectiveMode: EffectiveThemeMode,
    language: SupportedLanguage,
): void {
    if (typeof document === "undefined") return;

    const root = document.documentElement;
    root.classList.toggle("dark", effectiveMode === "dark");
    root.setAttribute("data-theme-mode", mode);
    root.setAttribute("lang", language);
}

export function PreferencesProvider({ children }: { children: ReactNode }) {
    const [language, setLanguageState] = useState<SupportedLanguage>(() =>
        normalizeLanguage(readStoredValue(LANGUAGE_STORAGE_KEY)),
    );
    const [themeMode, setThemeModeState] = useState<SupportedThemeMode>(() =>
        normalizeThemeMode(readStoredValue(THEME_STORAGE_KEY)),
    );
    const [effectiveThemeMode, setEffectiveThemeMode] = useState<EffectiveThemeMode>(() =>
        resolveEffectiveThemeMode(normalizeThemeMode(readStoredValue(THEME_STORAGE_KEY))),
    );

    useEffect(() => {
        applyToDom(themeMode, effectiveThemeMode, language);
    }, [themeMode, effectiveThemeMode, language]);

    // Follow the OS only while the user has not picked an explicit mode.
    useEffect(() => {
        if (themeMode !== "system" || typeof window === "undefined") return;

        const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        const handleChange = () => setEffectiveThemeMode(mediaQuery.matches ? "dark" : "light");

        handleChange();

        if (typeof mediaQuery.addEventListener === "function") {
            mediaQuery.addEventListener("change", handleChange);
            return () => mediaQuery.removeEventListener("change", handleChange);
        }

        mediaQuery.addListener(handleChange);
        return () => mediaQuery.removeListener(handleChange);
    }, [themeMode]);

    const setLanguage = useCallback((nextLanguage: SupportedLanguage) => {
        setLanguageState(nextLanguage);
        writeStoredValue(LANGUAGE_STORAGE_KEY, nextLanguage);
    }, []);

    const setThemeMode = useCallback((nextMode: SupportedThemeMode) => {
        setThemeModeState(nextMode);
        setEffectiveThemeMode(resolveEffectiveThemeMode(nextMode));
        writeStoredValue(THEME_STORAGE_KEY, nextMode);
    }, []);

    const value = useMemo<PreferencesContextValue>(
        () => ({ language, themeMode, effectiveThemeMode, setLanguage, setThemeMode }),
        [language, themeMode, effectiveThemeMode, setLanguage, setThemeMode],
    );

    return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences() {
    const context = useContext(PreferencesContext);
    if (!context) {
        throw new Error("usePreferences must be used within PreferencesProvider");
    }
    return context;
}
